import { useParams } from 'react-router-dom'
import { useProtocolMeter, useProcotol } from '@services/protocols/protocols.hook'
import { MeterTypes, METER_TYPE, PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import {
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
  Button,
  useMediaQuery
} from '@mui/material'
import { Edit, Close } from '@mui/icons-material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import dayjs, { Dayjs } from 'dayjs'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useEffect, useState } from 'react'
import UploadProgress from '@components/UploadProgress'
import NumericKeypad, { KEYPAD_BACKSPACE } from '@components/NumericKeypad'
import { uploadProgressStore } from '@services/uploadProgress/uploadProgress.store'

function ProtocolMeters({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { protocolMeter, updateProtocolMeter } = useProtocolMeter(Number(id))
  const { protocol, isLoading } = useProcotol(Number(id))
  const isTouchDevice = useMediaQuery('(pointer: coarse)')
  const [date, setDate] = useState<Dayjs | undefined>(
    dayjs(
      protocol?.protocolType === PROTOCOL_TYPE.acceptance
        ? protocol?.leaseStartDate
        : (protocolMeter.find((item) => item.readingDate)?.readingDate ?? undefined)
    )
  )
  const getValue = (meterValue: number | null) => (meterValue !== null ? meterValue : 'n/a')

  const sanitizeMeterValue = (value: string) => {
    const cleaned = value.replace(/[^0-9.,]/g, '')
    const separatorIndex = cleaned.search(/[.,]/)
    if (separatorIndex === -1) {
      return cleaned
    }
    return (
      cleaned.slice(0, separatorIndex + 1) + cleaned.slice(separatorIndex + 1).replace(/[.,]/g, '')
    )
  }

  const meterUpdateForm = useFormik({
    validateOnMount: false,
    validateOnChange: false,
    initialValues: {
      id: -1,
      meterType: METER_TYPE.hotWater,
      meterPhoto: null,
      meterValue: ''
    } as {
      id: number
      meterType: string
      meterPhoto: File | null
      meterValue: string
    },
    validationSchema: Yup.object().shape({
      meterType: Yup.string().required(),
      meterValue: Yup.string().when('id', {
        is: -1,
        then: (schema) => schema,
        otherwise: (schema) =>
          schema
            .required('Wartość odczytu jest wymagana')
            .matches(/^\d+([.,]\d+)?$/, 'Nieprawidłowa wartość odczytu')
      })
    }),
    onSubmit: async (values) => {
      if (values.id === -1) {
        protocolMeter.forEach(
          async (meter) =>
            await updateProtocolMeter.mutateAsync({
              id: meter.id,
              readingDate: (date as any).format('YYYY-MM-DD')
            })
        )
      } else {
        if (values.meterPhoto) {
          uploadProgressStore.setMeta({
            fileName: values.meterPhoto.name,
            fileIndex: 1,
            totalFiles: 1,
            estimatedTotal: values.meterPhoto.size
          })
        }

        await updateProtocolMeter.mutateAsync({
          id: values.id,
          meterType: values.meterType as MeterTypes,
          meterPhoto: values.meterPhoto ? values.meterPhoto : undefined,
          meterValue: parseFloat(values.meterValue.replace(',', '.')),
          readingDate: (date as any).format('YYYY-MM-DD')
        })
      }

      meterUpdateForm.resetForm()
    }
  })

  const handleKeypadKey = (key: string) => {
    const currentValue = meterUpdateForm.values.meterValue
    const nextValue =
      key === KEYPAD_BACKSPACE ? currentValue.slice(0, -1) : sanitizeMeterValue(currentValue + key)
    meterUpdateForm.setFieldValue('meterValue', nextValue)
  }

  useEffect(() => {
    if (protocolMeter.find((item) => item.readingDate)?.readingDate) {
      setDate(
        dayjs(
          protocol?.protocolType === PROTOCOL_TYPE.acceptance
            ? protocol?.leaseStartDate
            : protocolMeter.find((item) => item.readingDate)?.readingDate
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolMeter, protocol?.protocolType])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <form onSubmit={meterUpdateForm.handleSubmit}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='pl'>
          <DemoContainer components={['DatePicker']} sx={{ width: '100%', mb: 2 }}>
            <DatePicker
              label='Data odczytu'
              sx={{ width: '85%' }}
              onChange={(date) => setDate(date || undefined)}
              value={date}
              disabled={protocol?.protocolType === PROTOCOL_TYPE.acceptance}
            />
            <Button type='submit' variant='contained' loading={meterUpdateForm.isSubmitting}>
              Zapisz datę
            </Button>
          </DemoContainer>
        </LocalizationProvider>
        <TableContainer>
          <Table sx={{ minWidth: '100%' }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Typ</TableCell>
                <TableCell align='right'>Wartość</TableCell>
                <TableCell align='right'>Data odczytu</TableCell>
                <TableCell align='right'>Zdjęcie licznika</TableCell>
                {!isReadOnly && <TableCell align='right'></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {protocolMeter
                .filter(({ meterType }) => (meterType as any) !== METER_TYPE.gas)
                .map((meter) => (
                  <TableRow
                    key={meter.meterType}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      backgroundColor:
                        meterUpdateForm.values.id === meter.id ? 'rgba(0,0,0,0.1)' : undefined
                    }}
                  >
                    <TableCell component='th' scope='row'>
                      {meter.meterType}
                    </TableCell>
                    <TableCell align='right'>{getValue(meter.meterValue)}</TableCell>
                    <TableCell align='right'>{(date as any).format('YYYY-MM-DD')}</TableCell>
                    <TableCell align='right'>
                      {!!meter.meterPhoto && (
                        <Box
                          component='img'
                          src={meter.meterPhoto}
                          sx={{ maxWidth: '128px', height: 'auto' }}
                        />
                      )}
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell align='right'>
                        <IconButton
                          onClick={() =>
                            meterUpdateForm.setValues({
                              id: meter.id,
                              meterType: meter.meterType as any,
                              meterPhoto: null,
                              meterValue: meter.meterValue !== null ? String(meter.meterValue) : ''
                            })
                          }
                        >
                          <Edit />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {meterUpdateForm.values.id !== -1 && (
            <>
              <Typography variant='caption' fontWeight={700} display='flex' alignItems='center'>
                <IconButton size='small' onClick={() => meterUpdateForm.resetForm()}>
                  <Close />
                </IconButton>
                Edytuj wpis
              </Typography>
              <TextField
                fullWidth
                sx={{ my: 2 }}
                id='meterType'
                label='Typ licznika'
                variant='outlined'
                value={meterUpdateForm.values.meterType}
                disabled
              />
              <TextField
                error={!!meterUpdateForm.errors.meterValue}
                helperText={meterUpdateForm.errors.meterValue}
                fullWidth
                sx={{ my: 2 }}
                id='meterValue'
                label='Wartość odczytu'
                variant='outlined'
                slotProps={{
                  htmlInput: isTouchDevice
                    ? { inputMode: 'none', readOnly: true }
                    : { inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*' }
                }}
                onChange={({ target }) =>
                  meterUpdateForm.setFieldValue('meterValue', sanitizeMeterValue(target.value))
                }
                value={meterUpdateForm.values.meterValue}
                disabled={meterUpdateForm.isSubmitting}
              />
              {isTouchDevice && (
                <NumericKeypad
                  onKeyPress={handleKeypadKey}
                  disabled={meterUpdateForm.isSubmitting}
                />
              )}
              <TextField
                error={!!meterUpdateForm.errors.meterPhoto}
                helperText={meterUpdateForm.errors.meterPhoto}
                fullWidth
                sx={{ my: 2 }}
                id='meterPhoto'
                variant='outlined'
                onChange={({ target }: any) => {
                  meterUpdateForm.setFieldValue('meterPhoto', target.files?.[0])
                }}
                disabled={meterUpdateForm.isSubmitting}
                type='file'
              />
              <UploadProgress />
              <Button
                fullWidth
                type='submit'
                variant='contained'
                loading={meterUpdateForm.isSubmitting}
              >
                Zapis
              </Button>
            </>
          )}
        </TableContainer>
      </form>
    </>
  )
}

export default ProtocolMeters
